import {
    MediaProgramWithGuides,
    MediaProgramMetadata,
    MediaGuideMetadata,
} from '@/utils/media/types';
import {
    WorkflowWorkflowInfo,
    WorkflowWorkflowState,
    WorkflowWorkflowType,
} from '@calm/evexia-web-sdk';
import { logger } from '@rhg/libs/logger';
import { getGuideDuration } from '../media';
import { routes } from '../routes';

export enum ProgramMetadataCustomContentType {
    Mindshift = 'mindshift',
}

export enum ProgramState {
    NotJoined = 'not-joined',
    InProgress = 'in-progress',
    Completed = 'completed',
    Abandoned = 'abandoned',
    Unspecified = '',
}

export const workflowStateToProgramStateMap = {
    [WorkflowWorkflowState.Created]: ProgramState.InProgress,
    [WorkflowWorkflowState.InProgress]: ProgramState.InProgress,
    [WorkflowWorkflowState.Completed]: ProgramState.Completed,
    [WorkflowWorkflowState.Abandoned]: ProgramState.Abandoned,
};

export function getClinicalProgramState(workflow?: WorkflowWorkflowInfo) {
    if (!workflow) {
        return ProgramState.Unspecified;
    }

    if (!workflow?.workflowInstance) {
        return ProgramState.NotJoined;
    }

    return (
        workflowStateToProgramStateMap[
            workflow?.workflowInstance?.state ?? ''
        ] ?? ProgramState.Unspecified
    );
}

export function getProgramState(
    program: MediaProgramMetadata | MediaProgramWithGuides,
    workflow?: WorkflowWorkflowInfo,
    guides?: MediaProgramWithGuides['guides']
) {
    if (program.isClinical) {
        return getClinicalProgramState(workflow);
    }
    return program.completed
        ? ProgramState.Completed
        : (guides ?? (program as MediaProgramWithGuides).guides)?.some(
              (guide) => guide.completed
          )
        ? ProgramState.InProgress
        : ProgramState.Unspecified;
}

export function getProgramType(
    program?: MediaProgramWithGuides | MediaProgramMetadata,
    workflow?: WorkflowWorkflowInfo
) {
    return workflow?.workflowDefn.type ?? program?.isClinical
        ? WorkflowWorkflowType.ClinicalProgram
        : undefined;
}

export function getProgramSessionDurationRange(
    program: MediaProgramWithGuides
) {
    return program.guides.reduce(
        ({ min, max }, guide) => {
            const guideDuration = getGuideDuration(guide);
            return {
                min: Math.min(min, guideDuration ?? Infinity),
                max: Math.max(max, guideDuration ?? 0),
            };
        },
        { min: Infinity, max: 0 }
    );
}

/** will move completed programs to the back if willSortCompletion is true */
export const sortPrograms =
    (willSortCompletion = true) =>
    (a?: MediaProgramWithGuides, b?: MediaProgramWithGuides) => {
        const completionSort = willSortCompletion
            ? (a?.completed ? 1 : 0) - (b?.completed ? 1 : 0)
            : 0;
        return completionSort
            ? completionSort
            : a?.title?.localeCompare(b?.title ?? '') ?? 0;
    };

export function getIsJoined(workflow?: WorkflowWorkflowInfo) {
    return getClinicalProgramState(workflow) === ProgramState.InProgress;
}

function getNumberOfSessions(program: MediaProgramWithGuides) {
    return program?.guides?.filter((guide) => !guide.isTrailer).length ?? 0;
}

function getCompletedGuides(program: MediaProgramWithGuides) {
    return (
        program.guides?.filter(
            (guide) => guide.completed === true && !guide.isTrailer
        ) ?? []
    );
}

function isCurrentGuideHalfwayPoint(program: MediaProgramWithGuides) {
    const completeGuides = getCompletedGuides(program);
    const sessionCount = getNumberOfSessions(program);
    const halfwayPoint = Math.ceil(sessionCount / 2);

    const isCurrentlyLessThanHalf = completeGuides.length < halfwayPoint;
    const willBeHalfwayPoint = completeGuides.length + 1 === halfwayPoint;

    return isCurrentlyLessThanHalf && willBeHalfwayPoint;
}

export function getIsProgramHalfCompleted(
    program: MediaProgramWithGuides,
    currentGuide: MediaGuideMetadata
): boolean {
    if (!program.isClinical || program.guides?.length === 0) {
        return false; // Non-clinical programs aren't tracked for completion
    }

    return !currentGuide.completed && isCurrentGuideHalfwayPoint(program);
}

export function getProgramId(
    program: MediaProgramWithGuides | MediaProgramMetadata
) {
    return 'programId' in program
        ? program.programId
        : (program as { id: string }).id;
}

/**
 * Retrieves the `pack_class` metadata from the given program.
 *
 * @param {MediaProgramWithGuides | MediaProgramMetadata} [program] - The program object, which may contain metadata.
 * @returns {string | undefined} The `pack_class` value if it exists and is a string; otherwise, `undefined`.
 */
export function getProgramPackClassFromMetadata(
    program?: MediaProgramWithGuides | MediaProgramMetadata
): string | undefined {
    const packClass: unknown = program?.metadata?.pack_class;
    const result = typeof packClass === 'string' ? packClass : undefined;
    if (program?.isClinical && !result && process.env.NODE_ENV !== 'test') {
        const programId = getProgramId(program);
        logger.error(
            'Clinical Program Misconfigured',
            { type: 'CONTENT', programId },
            new Error('Clinical Program Misconfigured')
        );
    }
    return result;
}

/**
 * Retrieves the `workflow_id` metadata from the given program.
 *
 * @param {MediaProgramWithGuides | MediaProgramMetadata} [program] - The program object, which may contain metadata.
 * @returns {string | undefined} The `workflow_id` value if it exists and is a string; otherwise, `undefined`.
 */
export function getProgramWorkflowIdFromMetadata(
    program?: MediaProgramWithGuides | MediaProgramMetadata
): string | undefined {
    const workflowId: unknown = program?.metadata?.workflow_id;
    const result = typeof workflowId === 'string' ? workflowId : undefined;
    if (program?.isClinical && !result && process.env.NODE_ENV !== 'test') {
        const programId = getProgramId(program);
        logger.error(
            'Clinical Program Misconfigured',
            { type: 'CONTENT', programId },
            new Error('Clinical Program Misconfigured')
        );
    }
    return result;
}

/**
 * Creates the program details route for the provided program.
 * Prefers the pack_class in the program metadata, when present, for efficient retrieval of associated workflows and recommendations
 *
 * @param {MediaProgramWithGuides} [program] - The program object, which may contain metadata.
 * @param {packClass} [string] - The pack class that the program was sent in. Used as a fallback if the program has no metadata.
 * @param {showOverview} [string] - If present, forces the program page to show the overview tab, not the schedule tab
 * @param {additionalParams} [Record<string, string>] - Additional parameters to add to the route
 * @returns {string | undefined} The route (for use in e.g. href or router.push) for the program detail page
 */
export function getProgramRoute(
    program: MediaProgramWithGuides | MediaProgramMetadata,
    packClass?: string,
    showOverview?: string,
    additionalParams?: Record<string, string>
): string;
export function getProgramRoute(
    program?: MediaProgramWithGuides | MediaProgramMetadata,
    packClass?: string,
    showOverview?: string,
    additionalParams?: Record<string, string>
): string | undefined;
export function getProgramRoute(
    program?: MediaProgramWithGuides | MediaProgramMetadata,
    packClass?: string,
    showOverview = '',
    additionalParams = {}
): string | undefined {
    if (!program) {
        return undefined;
    }
    const packClassForPush =
        getProgramPackClassFromMetadata(program) ?? packClass;
    return routes.programDetails(
        packClassForPush,
        getProgramId(program),
        showOverview,
        additionalParams
    );
}

/**
 * Many V1 endpoints (e.g., GET bulk programs) do not work when seeing some pack classes
 * (seems to be: "non-database-backed" pack classes, like search right now)
 *
 * They also couple packClass with data where there should be no coupling
 * (e.g., packClass is saved along with a user favorite, so if the same
 * guide is returned in another pack class elsewhere, it won't show up as favorited)
 *
 * So: for now, remap "non-database-backed" pack classes to a "known good" pack class.
 * Make sure to do this everywhere the program or guide is coupled with the pack class.
 * This is a temporary workaround until we can fix the underlying issue, ideally in Media v2
 */
const packClassBlocklist = new Set([
    'clinical-search-results',
    'meditations-search-results',
    'sleep_stories-search-results',
    'music-search-results',
    'soundscapes-search-results',
    'breathing_exercises-search-results',
    'daily_programs-search-results',
    'wisdom-search-results',
    'movement-search-results',
    'kids-search-results',
    'more_content-search-results',
    'others-search-results',
    'ch-home-favorites',
    'ch-home-recents',
    'ch-home-todays-dailies',
    'ch-today-and-past-five-daily-calm',
]);
const knownGoodPackClass =
    process.env.NEXT_PUBLIC_DISCOVER_PAGE_BASE_PACK || 'calm-health-discover';
export function getRemappedPackClass(packClass: string) {
    return !packClass || packClassBlocklist.has(packClass)
        ? knownGoodPackClass
        : packClass;
}
